import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Calendar, ExternalLink, Loader2, MapPin, Users, Building2, 
  Sparkles, Target, Clock, ChevronRight, Bookmark, BookmarkCheck,
  Zap, Star, TrendingUp, Globe, ArrowRight, CheckCircle
} from "lucide-react";
import AppLayout from "@/components/AppLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getScoreColor = (score) => {
  if (score >= 80) return "text-green-600 bg-green-50 border-green-200";
  if (score >= 60) return "text-teal-600 bg-teal-50 border-teal-200";
  if (score >= 40) return "text-amber-600 bg-amber-50 border-amber-200";
  return "text-slate-600 bg-slate-50 border-slate-200";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateRange = (start, end) => {
  const startDate = formatDate(start);
  if (!end) return startDate;
  const endDate = formatDate(end);
  return `${startDate} - ${endDate}`;
};

function RecommendedEventCard({ event, onViewDetails, onSave, onUnsave, isSaved }) {
  return (
    <Card 
      className="border-2 border-teal-200 bg-gradient-to-br from-teal-50/50 to-white hover:shadow-lg transition-all cursor-pointer"
      onClick={() => onViewDetails(event)}
      data-testid={`recommended-event-${event.event_id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-slate-900">{event.name}</h3>
              </div>
              <div className="text-sm text-slate-500 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {event.location}
              </div>
            </div>
          </div>
          <div className={`px-3 py-1.5 rounded-lg border ${getScoreColor(event.match_score)}`}>
            <div className="text-lg font-bold">{event.match_score}%</div>
            <div className="text-xs">match</div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
          <Clock className="w-4 h-4" />
          <span>{formatDateRange(event.start_date, event.end_date)}</span>
          {event.days_until !== undefined && event.days_until <= 60 && (
            <Badge variant="outline" className="text-xs">
              {event.days_until} days away
            </Badge>
          )}
        </div>
        
        {/* Match reasons */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {event.match_reasons?.slice(0, 4).map((reason, i) => (
            <Badge key={i} variant="secondary" className="text-xs">
              <CheckCircle className="w-3 h-3 mr-1" />
              {reason}
            </Badge>
          ))}
        </div>
        
        {/* Strategic rationale */}
        {event.strategic_rationale && (
          <div className="p-3 bg-teal-50 border border-teal-100 rounded-lg mb-3">
            <p className="text-sm text-teal-800 line-clamp-3">
              <span className="font-medium">Why attend: </span>
              {event.strategic_rationale}
            </p>
          </div>
        )}
        
        {/* Key presence */}
        <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-3">
          {event.pharma_presence?.length > 0 && (
            <div className="flex items-center gap-1">
              <Building2 className="w-3.5 h-3.5" />
              {event.pharma_presence.length} pharma
            </div>
          )}
          {event.investor_presence?.length > 0 && (
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5" />
              {event.investor_presence.length} investors
            </div>
          )}
          {event.networking_score >= 80 && (
            <Badge className="bg-purple-100 text-purple-700 text-xs">Top networking</Badge>
          )}
        </div>
        
        <div className="flex justify-between items-center pt-3 border-t border-slate-100">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={(e) => { 
              e.stopPropagation(); 
              isSaved ? onUnsave(event.event_id) : onSave(event.event_id); 
            }}
            className={isSaved ? "text-amber-500" : "text-slate-400"}
          >
            {isSaved ? <BookmarkCheck className="w-4 h-4 mr-1" /> : <Bookmark className="w-4 h-4 mr-1" />}
            {isSaved ? "Saved" : "Save"}
          </Button>
          <Button variant="ghost" size="sm" className="text-teal-600 gap-1">
            View Details <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EventCard({ event, onViewDetails, onSave, onUnsave, isSaved }) {
  return (
    <Card 
      className="border border-slate-200 hover:border-teal-300 hover:shadow-md transition-all cursor-pointer"
      onClick={() => onViewDetails(event)}
      data-testid={`event-card-${event.event_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
            <Calendar className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-slate-900">{event.name}</h3>
                <div className="text-sm text-slate-500 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {event.location}
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={(e) => { 
                  e.stopPropagation(); 
                  isSaved ? onUnsave(event.event_id) : onSave(event.event_id); 
                }}
                className={isSaved ? "text-amber-500" : "text-slate-400 hover:text-amber-500"}
              >
                {isSaved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
              </Button>
            </div>
            <div className="flex items-center gap-2 mt-2 text-sm text-slate-600">
              <Clock className="w-3.5 h-3.5" />
              {formatDateRange(event.start_date, event.end_date)}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {event.themes?.slice(0, 3).map((theme, i) => (
                <Badge key={i} variant="secondary" className="text-xs">{theme}</Badge>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EventDetailDialog({ event, open, onClose, onSave, onUnsave, isSaved }) {
  const [loadingRationale, setLoadingRationale] = useState(false);
  const [aiRationale, setAiRationale] = useState(null);
  
  // Reset AI rationale when event changes
  useEffect(() => {
    setAiRationale(null);
  }, [event?.event_id]);
  
  const fetchAiRationale = async () => {
    if (!event) return;
    setLoadingRationale(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/${event.event_id}/rationale`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setAiRationale(data.rationale);
      }
    } catch (error) {
      console.error("Failed to fetch rationale:", error);
    } finally {
      setLoadingRationale(false);
    }
  };
  
  if (!event) return null;
  
  const rationale = aiRationale || event.strategic_rationale;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
              <Calendar className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-xl">{event.name}</DialogTitle>
              <DialogDescription className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {event.location}
              </DialogDescription>
            </div>
            {event.match_score && (
              <div className={`px-4 py-2 rounded-lg border ${getScoreColor(event.match_score)}`}>
                <div className="text-2xl font-bold">{event.match_score}%</div>
                <div className="text-xs">match</div>
              </div>
            )}
          </div>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Date & Registration */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="text-sm text-slate-500 mb-1">Dates</div>
              <div className="font-semibold text-slate-900">
                {formatDateRange(event.start_date, event.end_date)}
              </div>
              {event.days_until !== undefined && (
                <div className="text-sm text-teal-600 mt-1">{event.days_until} days away</div>
              )}
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="text-sm text-slate-500 mb-1">Registration</div>
              <div className="font-semibold text-slate-900">{event.registration_fee || "Contact organizer"}</div>
              {event.estimated_attendees && (
                <div className="text-sm text-slate-500 mt-1">{event.estimated_attendees} attendees</div>
              )}
            </div>
          </div>
          
          {/* Strategic Rationale */}
          <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-teal-900 flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Why You Should Attend
              </h4>
              {!aiRationale && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchAiRationale}
                  disabled={loadingRationale}
                  className="text-teal-700 hover:text-teal-900 text-xs"
                >
                  {loadingRationale ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3 h-3 mr-1" />
                      Get AI Analysis
                    </>
                  )}
                </Button>
              )}
            </div>
            <p className="text-teal-800">{rationale || "Click 'Get AI Analysis' for personalized insights based on your company profile."}</p>
          </div>
          
          {/* BD Opportunities */}
          {event.bd_opportunities && (
            <div className="p-4 bg-purple-50 border border-purple-100 rounded-lg">
              <h4 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                <Target className="w-4 h-4" />
                BD Opportunities
              </h4>
              <p className="text-purple-800">{event.bd_opportunities}</p>
            </div>
          )}
          
          {/* Description */}
          {event.description && (
            <div>
              <h4 className="font-semibold text-slate-900 mb-2">About This Event</h4>
              <p className="text-slate-600">{event.description}</p>
            </div>
          )}
          
          {/* Pharma Presence */}
          {event.pharma_presence?.length > 0 && (
            <div>
              <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Pharma & Biotech Presence
              </h4>
              <div className="flex flex-wrap gap-2">
                {event.pharma_presence.map((company, i) => (
                  <Badge key={i} variant="outline" className="text-sm">{company}</Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* Investor Presence */}
          {event.investor_presence?.length > 0 && (
            <div>
              <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Investors Expected
              </h4>
              <div className="flex flex-wrap gap-2">
                {event.investor_presence.map((investor, i) => (
                  <Badge key={i} className="bg-teal-100 text-teal-700">{investor}</Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* Key Speakers */}
          {event.key_speakers?.length > 0 && (
            <div>
              <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Key Speakers
              </h4>
              <div className="space-y-2">
                {event.key_speakers.map((speaker, i) => (
                  <div key={i} className="p-2 bg-slate-50 rounded-lg">
                    <div className="font-medium text-slate-900">{speaker.name}</div>
                    <div className="text-sm text-slate-500">{speaker.title}, {speaker.company}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Themes */}
          {event.themes?.length > 0 && (
            <div>
              <h4 className="font-semibold text-slate-900 mb-2">Topics & Themes</h4>
              <div className="flex flex-wrap gap-2">
                {event.themes.map((theme, i) => (
                  <Badge key={i} variant="secondary">{theme}</Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* Networking Score */}
          {event.networking_score && (
            <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <Star className="w-5 h-5 text-amber-500" />
              <div>
                <div className="font-medium text-slate-900">Networking Score: {event.networking_score}/100</div>
                <div className="text-sm text-slate-600">
                  {event.networking_score >= 90 ? "Exceptional networking opportunities" :
                   event.networking_score >= 80 ? "Excellent networking opportunities" :
                   event.networking_score >= 70 ? "Good networking opportunities" :
                   "Moderate networking opportunities"}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t">
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => isSaved ? onUnsave(event.event_id) : onSave(event.event_id)}
          >
            {isSaved ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
            {isSaved ? "Saved" : "Save Event"}
          </Button>
          {event.website && (
            <Button 
              className="flex-1 bg-teal-600 hover:bg-teal-700 gap-2" 
              onClick={() => window.open(event.website, '_blank')}
            >
              <Globe className="w-4 h-4" />
              Visit Website <ExternalLink className="w-4 h-4" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EventsContent() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [recommended, setRecommended] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [savedIds, setSavedIds] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [activeTab, setActiveTab] = useState("recommended");
  const [noProfile, setNoProfile] = useState(false);

  useEffect(() => {
    fetchEvents();
    fetchSaved();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/recommended`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setRecommended(data.recommended || []);
        setAllEvents(data.all_events || []);
        if (data.message) {
          setNoProfile(true);
        }
      }
    } catch (error) {
      console.error("Failed to fetch events:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSaved = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/saved`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setSavedIds(data.events?.map(e => e.event_id) || []);
      }
    } catch (error) {
      console.error("Failed to fetch saved events:", error);
    }
  };

  const handleSave = async (eventId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ event_id: eventId })
      });
      if (response.ok) {
        setSavedIds([...savedIds, eventId]);
        toast.success("Event saved");
      }
    } catch {
      toast.error("Failed to save event");
    }
  };

  const handleUnsave = async (eventId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/events/save/${eventId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (response.ok) {
        setSavedIds(savedIds.filter(id => id !== eventId));
        toast.success("Event removed");
      }
    } catch {
      toast.error("Failed to remove event");
    }
  };

  const handleViewDetails = (event) => {
    setSelectedEvent(event);
    setShowDetail(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="events-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3" data-testid="page-title">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-white" />
          </div>
          Events Intelligence
        </h1>
        <p className="text-slate-600 mt-2">
          AI-curated events matched to your business development goals
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="recommended" className="gap-2" data-testid="tab-recommended">
            <Sparkles className="w-4 h-4" />
            Don't Miss
          </TabsTrigger>
          <TabsTrigger value="saved" className="gap-2" data-testid="tab-saved">
            <Bookmark className="w-4 h-4" />
            Saved ({savedIds.length})
          </TabsTrigger>
          <TabsTrigger value="all" className="gap-2" data-testid="tab-all">
            <Calendar className="w-4 h-4" />
            All Events
          </TabsTrigger>
        </TabsList>

        {/* Recommended Tab */}
        <TabsContent value="recommended" className="space-y-6">
          {noProfile ? (
            <Card className="border-2 border-dashed">
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Get Personalized Recommendations</h3>
                <p className="text-slate-500 mb-4 max-w-md mx-auto">
                  Complete your company profile to receive AI-curated event recommendations tailored to your business development goals.
                </p>
                <Button onClick={() => navigate("/profile")} className="gap-2">
                  Complete Profile <ArrowRight className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Top 5 Recommended */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-500" />
                    Events You Shouldn't Miss
                  </h2>
                  <Badge variant="outline" className="text-sm">Top 5</Badge>
                </div>
                
                {recommended.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="p-8 text-center">
                      <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="font-semibold text-slate-900 mb-2">No upcoming events found</h3>
                      <p className="text-slate-500">Check back soon for new event recommendations.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {recommended.map((event) => (
                      <RecommendedEventCard
                        key={event.event_id}
                        event={event}
                        onViewDetails={handleViewDetails}
                        onSave={handleSave}
                        onUnsave={handleUnsave}
                        isSaved={savedIds.includes(event.event_id)}
                      />
                    ))}
                  </div>
                )}
              </div>
              
              {/* CTA */}
              {allEvents.length > 5 && (
                <Card className="border-2 border-dashed border-slate-200 bg-slate-50/50">
                  <CardContent className="p-6 text-center">
                    <Globe className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                    <h3 className="font-medium text-slate-900 mb-2">Explore All Events</h3>
                    <p className="text-slate-500 mb-4">{allEvents.length} events matched to your profile</p>
                    <Button variant="outline" onClick={() => setActiveTab("all")} className="gap-2">
                      View All <ChevronRight className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Saved Tab */}
        <TabsContent value="saved">
          {savedIds.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-12 text-center">
                <Bookmark className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-medium text-slate-900 mb-2">No saved events</h3>
                <p className="text-slate-500">Click the bookmark icon on any event to save it here</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allEvents
                .filter(event => savedIds.includes(event.event_id))
                .map((event) => (
                  <EventCard
                    key={event.event_id}
                    event={event}
                    onViewDetails={handleViewDetails}
                    onSave={handleSave}
                    onUnsave={handleUnsave}
                    isSaved={true}
                  />
                ))}
            </div>
          )}
        </TabsContent>

        {/* All Events Tab */}
        <TabsContent value="all">
          {allEvents.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-medium text-slate-900 mb-2">No upcoming events</h3>
                <p className="text-slate-500">Check back later for new events</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allEvents.map((event) => (
                <EventCard
                  key={event.event_id}
                  event={event}
                  onViewDetails={handleViewDetails}
                  onSave={handleSave}
                  onUnsave={handleUnsave}
                  isSaved={savedIds.includes(event.event_id)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Event Detail Dialog */}
      <EventDetailDialog
        event={selectedEvent}
        open={showDetail}
        onClose={() => setShowDetail(false)}
        onSave={handleSave}
        onUnsave={handleUnsave}
        isSaved={selectedEvent ? savedIds.includes(selectedEvent.event_id) : false}
      />
    </div>
  );
}

export default function Events() {
  return (
    <AppLayout>
      {() => <EventsContent />}
    </AppLayout>
  );
}
